import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/constants";
import { CalendarClock, Info } from "lucide-react";

export default function BiweeklyLab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/debts/progress").then(async r => {
      const plan = r.data.plan;
      const monthly = r.data.totals.monthly_commitment || 0;
      const snow = plan.snowball_amount || 0;
      if (!monthly) return;
      const extraBi = monthly / 12; // ~1 extra monthly payment per year
      const [base, bi] = await Promise.all([
        api.post("/debts/simulate", { strategy: plan.strategy, extra_monthly_payment: snow }),
        api.post("/debts/simulate", { strategy: plan.strategy, extra_monthly_payment: snow + extraBi }),
      ]);
      if (alive) setData({ monthly, base: base.data, bi: bi.data });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!data || !data.base.chosen.totals.months_to_debt_free) return null;
  const b = data.base.chosen.totals, x = data.bi.chosen.totals;
  const mSaved = b.months_to_debt_free - x.months_to_debt_free;
  const iSaved = Math.max(0, b.total_interest - x.total_interest);
  const half = data.monthly / 2;

  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid="biweekly-lab">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-cyan-500" />
        <div className="font-display font-bold text-lg">Biweekly payments</div>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Pay half your monthly total every 2 weeks. That's 26 half-payments a year — the equivalent of <b>one extra monthly payment</b> — with no change to your budget on a paycheck basis.
      </p>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Cell label="Pay every 2 weeks" value={fmt(half)} />
        <Cell label="Months saved" value={mSaved > 0 ? `${mSaved}` : "—"} tone="text-emerald-500" testid="biweekly-months-saved" />
        <Cell label="Interest saved" value={iSaved > 0 ? fmt(iSaved) : "—"} tone="text-emerald-500" testid="biweekly-interest-saved" />
      </div>
      <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
        <Info className="h-3 w-3 mt-0.5 shrink-0" /> Estimate. Set up biweekly autopay with each lender to capture this — OWEverit tracks the payments you log.
      </div>
    </div>
  );
}

function Cell({ label, value, tone = "", testid }) {
  return (
    <div className="p-3 rounded-xl bg-accent/40 border border-border/60" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className={`font-display font-mono font-bold text-lg mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
